import type { ReactNode } from 'react';
import type { TotalContent } from '@/blocks/domain/types';
import {
  resolveTotalPlacement,
  totalDisplayRows,
  totalPlacementCss,
  totalTokenRows,
  formatTotalRowValue
} from '@/blocks/domain/helpers/totalFields';
import { TEXT_COLORS } from '@/common/presentation/constants/colors';
import { getViewerLocale } from '@/viewer-locale/application/viewerLocaleStore';
import type { RendererProps } from '../../domain/RendererContract';
import { blockStyleToCss } from '../../application/blockStyleToCss';
import { resolveContextCurrency, resolveContextCultureTag } from '../../domain/formatDocumentMoneyCell';
import { keepsUnboundTokens } from '../../application/unboundTokenHtml';
import { useDocumentSurface } from '../primitives/useDocumentSurface';
import { renderUnboundTokens } from './fields/unboundTokenDisplay';
import { MONEY_CELL_STYLE } from './moneyCellStyles';

export function TotalRenderImpl({ content, context, style }: RendererProps<TotalContent>) {
  const viewerLocale = getViewerLocale();
  const currency = resolveContextCurrency(context as Record<string, unknown>, viewerLocale.currency);
  const cultureTag = resolveContextCultureTag(context as Record<string, unknown>, viewerLocale.cultureTag);
  // Sample data off (editor): the money cells are the `totals.*` variables, not an invented $0.00.
  const unbound = keepsUnboundTokens(context, useDocumentSurface());
  const rows: Array<{ label: string; emphasize: boolean; value: ReactNode }> = unbound
    ? totalTokenRows(content).map((row) => ({
        label: row.label,
        emphasize: row.emphasize,
        value: row.token ? renderUnboundTokens(`{{${row.token}}}`) : null
      }))
    : totalDisplayRows(content, context.totals).map((row) => ({
        label: row.label,
        emphasize: row.emphasize,
        value: formatTotalRowValue(row, currency, cultureTag)
      }));

  const computedMinHeight = 40 + rows.length * 20;
  const minHeight =
    typeof style.previewHeight === 'number' && style.previewHeight > 0
      ? Math.max(computedMinHeight, style.previewHeight)
      : computedMinHeight;

  return (
    <section
      aria-label="block-total"
      style={{
        ...blockStyleToCss(style),
        minHeight,
        width: '50%',
        ...totalPlacementCss(resolveTotalPlacement(content))
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={`${r.label}-${idx}`}>
              <td style={{ padding: '4px 0', color: r.emphasize ? style.textColor : TEXT_COLORS.MUTED }}>{r.label}</td>
              <td
                style={{
                  padding: '4px 0',
                  textAlign: 'right',
                  fontWeight: r.emphasize ? 700 : 600,
                  ...MONEY_CELL_STYLE
                }}
              >
                {r.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export const renderer = {
  Component: TotalRenderImpl,
  flowBehavior: 'atomic' as const
};

export type { TotalContent };
